import { useState } from 'react';
import { AxiosError } from 'axios';
import { Alert, Button, Dialog, DialogActions, DialogContent, DialogTitle } from '@mui/material';
import { CoInvestorDTO, InvestmentDTO, UpdateCoInvestorsRequestSchema } from '@inversor-hub/types';
import { useUpdateCoInvestors } from '@/lib/api/investments';
import useSnackbar from '@/hooks/useSnackbar';
import { CoInvestorsStep, CoInvestorFormRow } from './wizard/CoInvestorsStep';

interface Props {
  investment: InvestmentDTO;
  coInvestors: CoInvestorDTO[];
  onClose: () => void;
}

export const EditCoInvestorsDialog = ({ investment, coInvestors, onClose }: Props) => {
  const acquisitionPrice = investment.capex.acquisitionPrice;
  const [rows, setRows] = useState<CoInvestorFormRow[]>(() => {
    let assignedAmount = 0;
    return coInvestors.map((ci, index) => {
      const contributionAmount =
        index === coInvestors.length - 1
          ? acquisitionPrice - assignedAmount
          : Math.round(ci.ownershipPercentage * acquisitionPrice);
      assignedAmount += contributionAmount;
      return {
        email: ci.email,
        contributionAmount,
        ownershipPercentage: ci.ownershipPercentage,
        isPaid: ci.isPaid,
        feeType: ci.feeType ?? '',
        feeAmount: ci.feeAmount ?? null,
        feePercentage: ci.feePercentage ?? null,
      };
    });
  });
  const [error, setError] = useState<string | null>(null);
  const update = useUpdateCoInvestors();
  const { show } = useSnackbar();

  const handleSave = async () => {
    const parsed = UpdateCoInvestorsRequestSchema.safeParse({
      coInvestors: rows.map((row) => ({
        email: row.email,
        ownershipPercentage: row.ownershipPercentage,
      })),
    });
    if (!parsed.success) {
      setError(parsed.error.issues[0].message);
      return;
    }
    if (
      acquisitionPrice <= 0 ||
      Math.abs(
        rows.reduce((sum, row) => sum + (row.contributionAmount ?? 0), 0) - acquisitionPrice
      ) > 1
    ) {
      setError('La suma de los importes debe igualar el precio de adquisición');
      return;
    }
    setError(null);
    try {
      await update.mutateAsync({ investmentId: investment.id, data: parsed.data });
      show({ title: 'Coinversores y reparto actualizados', variant: 'success' });
      onClose();
    } catch (cause: unknown) {
      const message =
        cause instanceof AxiosError
          ? (cause.response?.data as { error?: string } | undefined)?.error
          : undefined;
      setError(message ?? 'No se han podido actualizar los coinversores');
    }
  };

  return (
    <Dialog
      open
      onClose={update.isPending ? undefined : onClose}
      maxWidth="md"
      fullWidth
      aria-labelledby="edit-co-investors-title"
    >
      <DialogTitle id="edit-co-investors-title">Editar coinversores y reparto</DialogTitle>
      <DialogContent>
        <Alert severity="info" sx={{ mb: 2 }}>
          Los nuevos participantes recibirán una invitación. Si cambia la participación de un
          coinversor, habrá que generar de nuevo su contrato pendiente de firma. Cambiar un email
          sustituye al participante.
        </Alert>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        <CoInvestorsStep
          coInvestors={rows}
          onChange={setRows}
          acquisitionPrice={acquisitionPrice}
          disabled={update.isPending}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={update.isPending}>
          Cancelar
        </Button>
        <Button variant="contained" onClick={handleSave} disabled={update.isPending}>
          {update.isPending ? 'Guardando…' : 'Guardar cambios'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
